import IJobSection from "../../interfaces/IJobSection";
import Icons from "../Icons";
import { tooltip } from "../../../../utils/tooltip";
import classNames from "classnames";
import { ll_url } from "../../../../utils/tools";
import * as Messages from "../../../../Messages";
import * as React from "react";
import { IBar, IFrontendResults } from "../../interfaces/frontend/IFrontendResults";
import { cmpVals } from "../CatalyticResiduesBoxContent";
import { InhibitorDiffOptions } from "../InhibitorDiffOptions";
import ChangeEnum from "../../interfaces/frontend/ChangeEnum";
import { CheckedSpinner } from "./checked-spinner";
import { useMemo } from "react";
import { useResultsContext } from "../../context/results-context";

interface Props {
  section: IJobSection,
  scrollToInhibitorList: () => void,
} 

export function SectionSummary({section, scrollToInhibitorList}: Props) {

  const {results} = useResultsContext();

  const renderDecision = (deleterious: boolean | undefined, labels?: {deleterious?: string, neutral?: string}) => {
    if (typeof deleterious !== 'boolean') {
      // We need non-breaking space for FoldX and Rosetta,
      // where the Decision element is the only element in the grid row.
      // Without using non-breaking space, the element would lose its height.
      return <>&nbsp;</>;
    }
    const icon = deleterious ? Icons.changeDeleterious : Icons.noChange;
    const text = deleterious ? (labels?.deleterious ?? "Deleterious") : (labels?.neutral ?? "Neutral");
    return <>
      {icon}
      <span style={{ marginLeft: '0.5rem' }}>{text}</span>
    </>
  }

  const { prediction, predictsnp, foldx, rosetta, conservation } = results;

  // Displays mutation string on screen (hides for printing to save width).
  const mutationScreen = <span className="ll-screen">{results.mutationStr}</span>

  const confidenceText = "Confidence"

  return (
    <div className="ll-section ll-summary ll-break-inside-avoid">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '5px',
      }} className="ll-section-header">
        <input className="ll-section-enable" type="checkbox" disabled checked />
        <span className="ll-section-name" style={{
          fontSize: '1.2rem',
          flex: '1',
        }}>{boldenSummarySectionName(section.name)}</span>
        <Legend/>
      </div>

      <div style={{
        padding: '5px',
      }} className="ll-result-summary">
        <div>
          <div className="ll-stability-row">
            <div className="ll-stability-grid" style={{ fontSize: '1.2em' }}>
              <div style={{ gridArea: 'title' }}>
                <span style={{ fontWeight: 'bold' }}>Impact - PredictONCO</span>
                {tooltip('summary_prediction')}
              </div>
              <div style={{ justifySelf: 'end', gridArea: 'top' }}>{mutationScreen}</div>

              <div style={{gridArea: 'decision'}}>
                {renderDecision(prediction?.deleterious, {deleterious: "Oncogenic", neutral: "Benign"})}
              </div>
              <div style={{gridArea: 'bottom'}} className="ll-screen">{confidenceText}</div>
              <div className="ll-plot" style={{gridArea: 'plot', height: '28px'}}>
                {renderStabilityBar(results, prediction?.bar)}
              </div>
            </div>
          </div>
        </div>
        <div className="ll-stability-row">
          <div className="ll-stability-grid">
            <div style={{ gridArea: 'title' }}>
              <span>Impact - PredictSNP</span>
              {tooltip('summary_predictsnp')}
            </div>
            <div style={{ justifySelf: 'end', gridArea: 'top' }}>{mutationScreen}</div>

            <div style={{gridArea: 'decision'}}>
              {renderDecision(results.predictsnp?.deleterious)}
            </div>
            <div style={{gridArea: 'bottom'}} className="ll-screen">{confidenceText}</div>
            <div className="ll-plot" style={{gridArea: 'plot'}}>
              {renderStabilityBar(results, predictsnp?.bar)}
            </div>
            <div style={{ gridArea: 'axis' }} className={classNames({ 'll-hidden': !(predictsnp?.jobId) })}>
              <a
                href={`https://loschmidt.chemi.muni.cz/predictsnp1/predictsnp.html?action=results&jobId=${predictsnp?.jobId || ""}`}
                target="_blank"
                style={{
                  display: 'block',
                  width: '100px',
                  margin: '0 auto',
                  padding: '2px',
                  boxSizing: 'content-box',
                }}
                className="ll-screen"
              >
                <img src={ll_url('/images/result/predictsnp.png')} style={{ width: '100%' }} alt="PredictSNP logo" />
              </a>
              <span className="ll-print ll-plot-axis">{confidenceText}</span>
            </div>
          </div>

          {/*results.flags.allowFoldx &&*/ <>
            <div className="ll-divider"><div /></div>
            <div className="ll-stability-grid">
              <div style={{ gridArea: 'title' }}>
                <span>Stability - FoldX</span>
                {tooltip('summary_foldx')}
              </div>
              <div style={{ gridArea: 'decision' }}>
                {renderDecision(foldx?.deleterious)}
              </div>
              <div style={{ gridArea: 'bottom' }}>{mutationScreen}</div>
              <div className="ll-plot" style={{gridArea: 'plot'}}>
                {renderStabilityBar(results, foldx?.bar, true)}
              </div>
              <div style={{ gridArea: 'axis' }} className={classNames('ll-plot-axis', {'ll-hidden': !foldx})}>
                <div className="ll-ticks">
                  <div className="ll-minus"><span>{foldx?.bounds.minus}</span></div>
                  <div className="ll-zero"><span>0</span></div>
                  <div className="ll-plus"><span>{foldx?.bounds.plus}</span></div>
                </div>
                <div className="ll-unit">kcal/mol</div>
              </div>
            </div>
          </>}

          {/*results.flags.allowRosetta &&*/ <>
            <div className="ll-divider"><div /></div>
            <div className="ll-stability-grid">
              <div style={{ gridArea: 'title' }}>
                <span>Stability - Rosetta</span>
                {tooltip('summary_rosetta')}
              </div>
              <div style={{ gridArea: 'decision' }}>
                {renderDecision(rosetta?.deleterious)}
              </div>
              <div style={{ gridArea: 'bottom' }}>{mutationScreen}</div>
              <div className="ll-plot" style={{gridArea: 'plot'}}>
                {renderStabilityBar(results, rosetta?.bar, true)}
              </div>
              <div style={{ gridArea: 'axis' }} className={classNames('ll-plot-axis', {'ll-hidden': !rosetta})}>
                <div className="ll-ticks">
                  <div className="ll-minus"><span>{rosetta?.bounds.minus}</span></div>
                  <div className="ll-zero"><span>0</span></div>
                  <div className="ll-plus"><span>{rosetta?.bounds.plus}</span></div>
                </div>
                <div className="ll-unit">kcal/mol</div>
              </div>
            </div>
          </>}
        </div>

        <div style={{
          display: 'flex',
          margin: '20px 1rem 1rem',
          justifyContent: 'space-evenly',
        }}>
          <div className="ll-center">
            <div className="ll-column-header">
              <span>Mutation position</span>
              {tooltip('summary_mutation_position')}
            </div>
            <div style={{textAlign: 'center'}}>
              {results.mutation !== null ? (
                <span className={classNames( {"ll-result-red-text": results.mutation.essential })}>
                  {results.mutation.essential ? 'Essential' : 'Non-essential'} residue
                  <br />
                  in {results.mutation.domainType} domain
                </span>
              ) : <CheckedSpinner withText={false}/>}
            </div>
          </div>
          {conservation && (
            <div className="ll-center">
              <div className="ll-column-header">
                <span>Conservation</span>
                {tooltip('summary_conservation')}
              </div>
              <div style={{textAlign: 'center'}}>
                                    <span>
                                        {conservation} evolutionary<br />conservation
                                    </span>
              </div>
            </div>
          )}
          {/*results.flags.allowPka && */ <CatalyticResiduesSummary />}
          {/*results.flags.allowInhibitors && */ <InhibitorsSummary scrollToInhibitorList={scrollToInhibitorList} />}
        </div>

        {!results.structure && (
          <div className="ll-summary-msg">
            {Messages.JOB_NO_STRUCT_FOR_RESIDUE()}
          </div>
        )}
      </div>
    </div>
  )
}

function boldenSummarySectionName(name: string) {
  const m = name.match(/(Summary for )(.+)/);
  if (m === null) {
    return name;
  }
  return <>{m[1]}<strong>{m[2]}</strong></>
}

function renderStabilityBar(
  results: IFrontendResults,
  data: IBar | null | undefined,
  requireStructure: boolean = false
) {
  return (
    requireStructure && !results.structure ? (
      <div style={{flex: 1}}>
        {Messages.warnIcon}
      </div>
    ) : data ? (
      data.filter(item => item.width > 0).map((item, idx) => (
        <div key={idx} className={classNames(
          "ll-single-bar-text", {
            'll-center': item.content !== null && item.content.align === "center",
            'll-left': item.content !== null && item.content.align === "left",
            'll-right': item.content !== null && item.content.align === "right",
          }, item.className
        )} style={{
          flex: item.width,
          backgroundColor: item.backgroundColor ?? undefined,
        }}>
          {item.content !== null ? <span>{item.content.text}</span> : null}
        </div>
      ))
    ) : (
      <div style={{flex: 1}}>
        <CheckedSpinner withText={false}/>
      </div>
    )
  )
}

function CatalyticResiduesSummary() {
  const {results} = useResultsContext();
  const structure = results.structure;
  const finishedAndStructure = structure && Array.isArray(results.catalyticResidues);
  const noChanges = finishedAndStructure && results.catalyticResidues.length === 0;
  return (
    <div className="ll-center">
      <div className="ll-column-header">
        <span>Catalytic residues</span>
        {tooltip('summary_catalytic_residues')}
        <br />
        <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>
          {noChanges ? "No changes in pKa" : "Changes in pKa"}
        </span>
      </div>
      {!structure ? (
        <div style={{ textAlign: 'center' }}>
          {Messages.warnIcon}
        </div>
      ) : finishedAndStructure ? (
        <div className={classNames("ll-summary-residues", {
          "ll-grid-3-cols": results.catalyticResidues.length >= 5 && results.catalyticResidues.length <= 6,
          "ll-grid-4-cols": results.catalyticResidues.length >= 7,
        })}>
          {results.catalyticResidues.map(cmpItem => (
            <React.Fragment key={cmpItem.name}>
              {cmpVals(cmpItem, Icons).icon}
              <span>{cmpItem.name}</span>
            </React.Fragment>
          ))}
        </div>
      ) : ( /* has structure but calculation is not finished yet */
        <div style={{ textAlign: 'center' }}>
          <CheckedSpinner withText={false}/>
        </div>
      )}
    </div>
  )
}

function InhibitorsSummary({scrollToInhibitorList}: {scrollToInhibitorList: () => void}) {
  const {results, setInhibitorDiffFilter} = useResultsContext();
  const {
    structure,
    inhibitors,
  } = results;
  const diffOptionAndCountArr = useMemo(() => {
    if (!inhibitors) return [];
    return InhibitorDiffOptions.map(option => {
      const cnt = inhibitors.filter(inh => inh.bindingEnergyDiffClass === option.value).length;
      return [option, cnt] as const;
    });
  }, [inhibitors]);
  return (
    <div className="ll-center">
      <div className="ll-column-header">
        <span>Inhibitors</span>
        {tooltip('summary_inhibitors')}
        <br />
        <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>Changes in binding energy</span>
      </div>
      {!structure ? (
        <div style={{ textAlign: 'center' }}>
          {Messages.warnIcon}
        </div>
      ) : inhibitors ? (
        <div className="ll-icon-row-icon-first-groupped">
          {diffOptionAndCountArr.map(([option, cnt]) => {
            const onClickHandler = cnt > 0 ? () => {
              setInhibitorDiffFilter(option.value);
              scrollToInhibitorList();
            } : undefined;
            const title = `Navigate to inhibitors with ${option.label.toLowerCase()}`;
            const wrapCls = classNames(option.textCls, {'ll-click-inh-diff': onClickHandler !== undefined});
            return (
              <span key={ChangeEnum[option.value]} className={wrapCls} onClick={onClickHandler} title={title}>
                {Icons[option.icon]}
                <span>{cnt}</span>
              </span>
            )
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <CheckedSpinner withText={false}/>
        </div>
      )}
    </div>
  )
}


function Legend() {
  return (
    <div className="ll-legend">
      <div className="ll-legend-grid">
        <div className="ll-legend-item">
          {Icons.increaseLarge}
          <span>Large Increase</span>
        </div>
        <div className="ll-legend-item">
          {Icons.decreaseLarge}
          <span>Large Decrease</span>
        </div>
        <div className="ll-legend-item">
          {Icons.increaseModerate}
          <span>Moderate Increase</span>
        </div>
        <div className="ll-legend-item">
          {Icons.decreaseModerate}
          <span>Moderate Decrease</span>
        </div>
      </div>
      <div className="ll-legend-item ll-no-change">
        {Icons.noChange}
        <span>No change</span>
      </div>
      <div className="ll-legend-item ll-deleterious">
        {Icons.changeDeleterious}
        <span>Deleterious</span>
      </div>
    </div>
  )
}